import {
  DataTable,
  IconButton,
  Page,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useMemo } from 'react';

import { facebookMessengerApi } from 'apis/facebookMessenger';
import { WhatsAppPhoneNumber, whatsAppTwilioApi } from 'apis/whatsAppTwilio';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { MessagingQueueDetails } from '../../queueDetails/messagingQueueDetails';
import { WhatsAppSignUpData } from '../../whatsAppLoginButton';

import { WhatsAppAuthenticator } from './components/whatsAppAuthenticator/whatsAppAuthenticator';
import { WhatsAppEditPhoneNumberQueueModal } from './components/whatsAppEditPhoneNumberQueueModal/whatsAppEditPhoneNumberQueueModal';
import { AddWhatsAppPhoneNumberModal } from './components/whatsAppNumberModal/whatsAppNumberModal';

type GroupedByWabaId = {
  [key: string]: WhatsAppPhoneNumber[];
};

const groupByWabaId = (
  phoneNumbers: WhatsAppPhoneNumber[],
): GroupedByWabaId => {
  return phoneNumbers.reduce((acc, phoneNumber) => {
    const { waba_id } = phoneNumber;
    if (!acc[waba_id]) {
      acc[waba_id] = [];
    }
    acc[waba_id].push(phoneNumber);
    return acc;
  }, {} as GroupedByWabaId);
};

/** Represents the WhatsApp section. */
export const WhatsAppTwilio = (): JSX.Element | null => {
  const dispatch = useAppDispatch();

  const { facebookApp } = facebookMessengerApi.useReadApplicationsQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        facebookApp: data?.data.applications?.find(
          (app) => app.type === 'common',
        ),
      }),
    },
  );

  const { whatsAppPhoneNumbers, isLoading, isError } =
    whatsAppTwilioApi.useReadWhatsAppPhoneNumbersQuery(undefined, {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        whatsAppPhoneNumbers: data?.data.phone_numbers || [],
      }),
    });

  const groupedPhoneNumbers = useMemo(
    () => groupByWabaId(whatsAppPhoneNumbers),
    [whatsAppPhoneNumbers],
  );

  const handleEditQueue = (phoneNumber: WhatsAppPhoneNumber) => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:whatsAppEditPhoneNumberQueue',
          arg: { phoneNumber },
        },
      }),
    );
  };

  const dataTables = useMemo(
    () =>
      Object.entries(groupedPhoneNumbers).map(([wabaId, phoneNumbers]) => (
        <div key={wabaId}>
          <Page.Section title={`${wabaId}`}>
            <DataTable
              data={phoneNumbers}
              columns={[
                {
                  label: 'Number',
                  selector: (phoneNumber) => phoneNumber.phone_number,
                  style: {
                    width: '40%',
                  },
                },
                {
                  label: 'Status',
                  selector: (phoneNumber) => phoneNumber.status,
                  style: {
                    width: '30%',
                  },
                },
                {
                  label: 'Queue',
                  selector: (phoneNumber) => (
                    <MessagingQueueDetails
                      queueId={phoneNumber.default_queue_id}
                    />
                  ),
                  style: {
                    width: '30%',
                  },
                },
                {
                  label: '',
                  selector: (phoneNumber) => (
                    <Tooltip content="Edit queue">
                      <IconButton
                        variant="tertiary"
                        icon="pen"
                        onClick={() => handleEditQueue(phoneNumber)}
                      />
                    </Tooltip>
                  ),
                  style: {
                    width: 0,
                  },
                },
              ]}
            />
          </Page.Section>
        </div>
      )),
    [groupedPhoneNumbers],
  );

  const handleAddPhoneNumber = async ({
    wabaId,
  }: {
    wabaId: WhatsAppSignUpData['waba_id'];
  }) => {
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:addWhatsAppNumberModal',
          arg: {
            wabaId,
          },
        },
      }),
    );
  };

  if (!facebookApp || !whatsAppPhoneNumbers) return null;

  return (
    <>
      <Page.Section
        title="WhatsApp"
        controls={
          <WhatsAppAuthenticator
            appId={facebookApp?.id}
            facebookButtonProps={{
              variant: 'secondary',
              platform: 'whatsapp',
              icon: 'plus',
            }}
            handleAddPhoneNumber={handleAddPhoneNumber}
          >
            WhatsApp number
          </WhatsAppAuthenticator>
        }
      >
        {(isLoading || whatsAppPhoneNumbers.length === 0) && (
          <Table
            loading={isLoading}
            loadingFailed={isError}
            emptyState={{
              icon: 'facebook-messenger',
              title: 'Get started with WhatsApp',
              description:
                'Click the "+ WhatsApp number" button above and we\'ll help you through the setup process of the WhatsApp channel.',
              buttonProps: {
                children: 'Learn more',
                variant: 'secondary',
                onClick: () => elevioClient.openArticle('1023'),
              },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th width="40%">Number</Table.Th>
                <Table.Th width="30%">Status</Table.Th>
                <Table.Th width="30%">Queue</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody></Table.Tbody>
          </Table>
        )}
      </Page.Section>

      {dataTables}

      <AddWhatsAppPhoneNumberModal />
      <WhatsAppEditPhoneNumberQueueModal />
    </>
  );
};
